import { useState } from 'react';
import {
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { CreditCard, AlertCircle } from 'lucide-react';

interface PaymentMethodFormProps {
  clientSecret: string;
  onSuccess: (paymentMethodId: string) => void;
  onCancel?: () => void;
  submitLabel?: string;
  loading?: boolean;
}

/**
 * Form for collecting payment method using Stripe Elements.
 * Uses CardElement for card number, expiry, and CVC input.
 */
export function PaymentMethodForm({
  clientSecret,
  onSuccess,
  onCancel,
  submitLabel = 'Add Payment Method',
  loading: externalLoading,
}: PaymentMethodFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found. Please refresh and try again.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Confirm the SetupIntent to save the payment method
      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (confirmError) {
        setError(confirmError.message || 'Failed to save payment method');
        return;
      }

      if (setupIntent?.payment_method) {
        // Payment method saved successfully
        onSuccess(setupIntent.payment_method as string);
      } else {
        setError('Payment method was not saved. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  const isLoading = isProcessing || externalLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="rounded-lg border bg-card p-4">
        <label className="mb-2 block text-sm font-medium">
          Card Information
        </label>
        <div className="rounded-btn border bg-background p-3">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: 'hsl(var(--foreground))',
                  fontFamily: 'inherit',
                  '::placeholder': {
                    color: 'hsl(var(--muted-foreground))',
                  },
                },
                invalid: {
                  color: 'hsl(var(--destructive))',
                },
              },
              hidePostalCode: true,
            }}
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Your card will be securely saved for future payments.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-btn bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex gap-3">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1"
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={!stripe || isLoading}
          loading={isLoading}
          className="flex-1"
        >
          <CreditCard className="mr-2 h-4 w-4" />
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
